import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled, { useTheme } from "styled-components";
import { usePopper } from "react-popper";
import CloseIcon from "../icons/close";

export const Arrow = styled.div`
  &::before {
    content: "";
    background: white;
    width: 10px;
    height: 10px;
    transform: translate(-50%, -50%) rotate(45deg);
    position: absolute;
    top: 0;
    left: 0;
  }
  &[data-arrow-placement^="right"]::before {
    border: 1px solid ${({ theme }) => theme.themeColors.lightGrey};
    border-right: 1px solid transparent;
    border-top: 1px solid transparent;
  }
  &[data-arrow-placement^="left"]::before {
    border: 1px solid ${({ theme }) => theme.themeColors.lightGrey};
    border-left: 1px solid transparent;
    border-bottom: 1px solid transparent;
  }
  &[data-arrow-placement^="top"]::before {
    border: 1px solid ${({ theme }) => theme.themeColors.lightGrey};
    border-left: 1px solid transparent;
    border-top: 1px solid transparent;
  }
  &[data-arrow-placement^="bottom"]::before {
    border: 1px solid ${({ theme }) => theme.themeColors.lightGrey};
    border-right: 1px solid transparent;
    border-bottom: 1px solid transparent;
  }
`;

export const PopoverWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lightGrey};
  background: white;
  border-radius: 12px;
  padding: 1rem;
  max-width: 35rem;
  font-weight: 600;
  z-index: 3;
  box-shadow: 0 2px 6px rgba(138, 168, 181, 0.2);
  margin-left: calc(1.5rem - 2px);
  width: 100%;

  &[data-popper-placement^="right"] {
    ${Arrow} {
      left: 0;
    }
  }
  &[data-popper-placement^="left"] {
    ${Arrow} {
      right: 0;
    }
  }
  &[data-popper-placement^="top"] {
    ${Arrow} {
      bottom: 0;
    }
  }
  &[data-popper-placement^="bottom"] {
    ${Arrow} {
      top: 0;
    }
  } 
  ${({ theme }) => theme.mq.sm`
    margin: 0;
    width: auto;
  `}
`;

const CloseWrapper = styled.div`
  float: right;
  display: block;
  ${({ theme }) => theme.mq.md`
    display: none;
  `}
`;

function PopoverComponent({ placement, children, referenceElement, showClose }) {
  const [show, setShow] = useState(false);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const theme = useTheme();

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      { name: "arrow", options: { element: arrowElement } },
      { name: "offset", options: { offset: [0, 12] } },
    ],
  });

  useEffect(() => {
    // named functions so they can be cleaned up after component teardown
    function showPopover() {
      setShow(true);
    }
    function hidePopover() {
      setShow(false);
    }
    if (referenceElement) {
      referenceElement.addEventListener("mouseenter", showPopover);
      referenceElement.addEventListener("mouseleave", hidePopover);
      return function cleanupListeners() {
        referenceElement.removeEventListener("mouseenter", showPopover);
        referenceElement.removeEventListener("mouseleave", hidePopover);
      };
    }
    return function emptyResult() {};
  }, [referenceElement]);

  return (
    <>
      {show && (
        <PopoverWrapper
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
        >
          {showClose && <CloseWrapper>
            <CloseIcon name="close" width="16" height="16" color={`${theme.themeColors.darkGreen3}`}/>
          </CloseWrapper>}
          {children}
          <Arrow
            ref={setArrowElement}
            style={styles.arrow}
            data-arrow-placement={placement}
          />
        </PopoverWrapper>
      )}
    </>
  );
}

export default PopoverComponent;

PopoverComponent.propTypes = {
  placement: PropTypes.oneOf(["top", "right", "bottom", "left"]),
  referenceElement: PropTypes.any,
  children: PropTypes.element.isRequired,
  showClose: PropTypes.bool,
};

PopoverComponent.defaultProps = {
  placement: "right",
  referenceElement: undefined,
  showClose: false,
};
